import React, { useState, useEffect, useCallback } from 'react';
import {
  FiRefreshCw,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiClock,
  FiLink,
  FiUploadCloud,
  FiDownloadCloud,
  FiExternalLink,
  FiX,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import toast from 'react-hot-toast';
import {
  getReconciliationReport,
  runReconciliation,
  syncAllPending,
  importBedrockPrompt,
  syncPromptToBedrock,
  resolveBedrockConflict,
} from '../../../../../services/aiOperationsApi';
import BedrockErrorAlert from './BedrockErrorAlert';

type IssueType = 'sync_error' | 'orphaned_in_aivilo' | 'untracked_in_bedrock' | 'external_changes';
type IssueSeverity = 'error' | 'warning' | 'info';
type VariantType = 'default' | 'success' | 'warning' | 'error' | 'info';

interface Issue {
  type: IssueType;
  severity: IssueSeverity;
  prompt_id?: string;
  prompt_name?: string;
  bedrock_prompt_id?: string;
  bedrock_prompt_name?: string;
  mapping_id?: string;
  description: string;
  action_required?: string;
  consecutive_failures?: number;
}

interface HealthyPrompt {
  prompt_name: string;
  bedrock_version?: string;
  last_synced_at?: string;
}

interface ReportSummary {
  synced_correctly?: number;
  sync_errors?: number;
  external_changes?: number;
  orphaned_in_aivilo?: number;
  untracked_in_bedrock?: number;
  total_aivilo_prompts?: number;
  total_bedrock_prompts?: number;
}

interface ReconciliationReport {
  summary?: ReportSummary;
  issues?: Issue[];
  healthy_prompts?: HealthyPrompt[];
  recommendations?: string[];
  generated_at?: string;
}

interface SyncResults {
  successful: number;
  failed: number;
  total_attempted: number;
}

interface SummaryCardProps {
  title: string;
  count: number;
  icon: IconType;
  variant?: VariantType;
  description?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, count, icon: Icon, variant = 'default', description }) => {
  const variants: Record<VariantType, string> = {
    default: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400',
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400',
  };

  return (
    <div className={`p-4 rounded-lg border ${variants[variant]}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <div>
          <p className="text-2xl font-bold">{count}</p>
          <p className="text-sm opacity-80">{title}</p>
          {description && (
            <p className="text-xs opacity-60 mt-0.5">{description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

interface IssueRowProps {
  issue: Issue;
  onAction: (issue: Issue, action: string) => void;
  actionLoading: boolean;
}

interface IssueConfig {
  icon: IconType;
  color: string;
  bg: string;
  border: string;
  label: string;
}

const getIssueConfig = (type: IssueType): IssueConfig => {
  switch (type) {
    case 'sync_error':
      return {
        icon: FiXCircle,
        color: 'text-red-500',
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        label: 'Sync Error',
      };
    case 'orphaned_in_aivilo':
      return {
        icon: FiUploadCloud,
        color: 'text-amber-500',
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        label: 'Not in Bedrock',
      };
    case 'untracked_in_bedrock':
      return {
        icon: FiDownloadCloud,
        color: 'text-blue-500',
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        label: 'Not in Aivilo',
      };
    case 'external_changes':
      return {
        icon: FiAlertTriangle,
        color: 'text-orange-500',
        bg: 'bg-orange-50 dark:bg-orange-900/20',
        border: 'border-orange-200 dark:border-orange-800',
        label: 'External Changes',
      };
    default:
      return {
        icon: FiClock,
        color: 'text-gray-500',
        bg: 'bg-gray-50 dark:bg-gray-800',
        border: 'border-gray-200 dark:border-gray-700',
        label: 'Unknown',
      };
  }
};

const severityColorClass = (severity: IssueSeverity): string => {
  if (severity === 'error') return 'text-red-500';
  if (severity === 'warning') return 'text-amber-500';
  return 'text-blue-500';
};

const IssueRow: React.FC<IssueRowProps> = ({ issue, onAction, actionLoading }) => {
  const config = getIssueConfig(issue.type);
  const Icon = config.icon;

  const renderActionButton = () => {
    if (['sync_error', 'orphaned_in_aivilo'].includes(issue.type)) {
      return (
        <button
          onClick={() => onAction(issue, 'sync')}
          disabled={actionLoading}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
        >
          Sync to Bedrock
        </button>
      );
    }
    if (issue.type === 'untracked_in_bedrock') {
      return (
        <button
          onClick={() => onAction(issue, 'import')}
          disabled={actionLoading}
          className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 transition-colors"
        >
          Import to Aivilo
        </button>
      );
    }
    if (issue.type === 'external_changes') {
      return (
        <div className="flex gap-2">
          <button
            onClick={() => onAction(issue, 'use_bedrock')}
            disabled={actionLoading}
            className="px-3 py-1.5 text-xs font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 rounded-lg disabled:opacity-50 transition-colors"
          >
            Use Bedrock
          </button>
          <button
            onClick={() => onAction(issue, 'use_aivilo')}
            disabled={actionLoading}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg disabled:opacity-50 transition-colors"
          >
            Keep Aivilo
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`p-4 rounded-lg border ${config.bg} ${config.border}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${config.color}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${config.bg} ${config.color}`}>
                {config.label}
              </span>
              <span className={`text-xs ${severityColorClass(issue.severity)}`}>
                {issue.severity}
              </span>
            </div>
            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white truncate">
              {issue.prompt_name ?? issue.bedrock_prompt_name ?? 'Unknown'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {issue.description}
            </p>
            {issue.action_required && (
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 italic">
                Action: {issue.action_required}
              </p>
            )}
            {(issue.consecutive_failures ?? 0) > 0 && (
              <p className="text-xs text-red-500 mt-1">
                {issue.consecutive_failures} consecutive failure{issue.consecutive_failures === 1 ? '' : 's'}
              </p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          {renderActionButton()}
        </div>
      </div>
    </div>
  );
};

interface HealthyPromptRowProps {
  prompt: HealthyPrompt;
}

const formatRelativeTime = (dateString: string | undefined): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const HealthyPromptRow: React.FC<HealthyPromptRowProps> = ({ prompt }) => {
  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {prompt.prompt_name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Version {prompt.bedrock_version ?? 'DRAFT'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span>{formatRelativeTime(prompt.last_synced_at)}</span>
        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
          Synced
        </span>
      </div>
    </div>
  );
};

interface BedrockReconciliationReportProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

const BedrockReconciliationReport: React.FC<BedrockReconciliationReportProps> = ({ isOpen, onClose, onRefresh }) => {
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [showHealthy, setShowHealthy] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [syncResults, setSyncResults] = useState<SyncResults | null>(null);

  const fetchReport = useCallback(async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      const data = await getReconciliationReport();
      setReport(data);
    } catch (err: unknown) {
      console.error('Failed to fetch reconciliation report:', err);
      setError(err instanceof Error ? err.message : String(err));
      if (!isInitialLoad) {
        toast.error('Failed to refresh sync status');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchReport(true);
    }
  }, [isOpen, fetchReport]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const response = await runReconciliation();
      const reportData = response?.report ?? response;
      setReport(reportData);
      toast.success('Sync status refreshed');
    } catch (err: unknown) {
      console.error('Reconciliation failed:', err);
      setError(err instanceof Error ? err.message : String(err));
      toast.error('Failed to refresh sync status');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSyncAllPending = async () => {
    try {
      setSyncingAll(true);
      setError(null);
      setSyncResults(null);
      const results: SyncResults = await syncAllPending();
      setSyncResults(results);

      if (results.successful > 0) {
        toast.success(`${results.successful} prompt${results.successful > 1 ? 's' : ''} synced successfully`);
      }
      if (results.failed > 0) {
        toast.error(`${results.failed} prompt${results.failed > 1 ? 's' : ''} failed to sync`);
      }

      await fetchReport();
      if (onRefresh) onRefresh();
    } catch (err: unknown) {
      console.error('Sync all failed:', err);
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error(message || 'Failed to sync prompts');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleIssueAction = async (issue: Issue, action: string) => {
    const promptName = issue.prompt_name ?? issue.bedrock_prompt_name ?? 'Prompt';

    try {
      setActionLoading(true);
      setError(null);

      if (action === 'sync') {
        if (!issue.prompt_id) {
          setError('Cannot sync: prompt_id is missing from the issue data');
          toast.error('Cannot sync: missing prompt ID');
          return;
        }
        const result = await syncPromptToBedrock(issue.prompt_id);
        if (result?.success === false) {
          setError(result.message || 'Sync failed. Check if Bedrock credentials are configured.');
          toast.error(result.message || 'Sync failed');
          return;
        }
        toast.success(`"${promptName}" synced to Bedrock`);
      } else if (action === 'import' && issue.bedrock_prompt_id) {
        await importBedrockPrompt(issue.bedrock_prompt_id, { environment: 'development' });
        toast.success(`"${promptName}" imported to Aivilo`);
      } else if (['use_bedrock', 'use_aivilo'].includes(action) && issue.mapping_id) {
        await resolveBedrockConflict(issue.mapping_id, action);
        const resolution = action === 'use_bedrock' ? 'Bedrock version applied' : 'Aivilo version kept';
        toast.success(`"${promptName}": ${resolution}`);
      }

      await fetchReport();
      if (onRefresh) onRefresh();
    } catch (err: unknown) {
      console.error('Action failed:', err);
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error(message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  if (!isOpen) return null;

  const summary = report?.summary ?? {};
  const issues = report?.issues ?? [];
  const healthyPrompts = report?.healthy_prompts ?? [];
  const recommendations = report?.recommendations ?? [];
  const issueCount = (summary.sync_errors ?? 0) + (summary.external_changes ?? 0) + (summary.orphaned_in_aivilo ?? 0);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" aria-labelledby="reconciliation-title" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-gray-500/75 dark:bg-gray-900/80 transition-opacity" onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }} role="presentation" />

      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-2xl">
          <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 id="reconciliation-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                  Bedrock Sync Status
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Reconciliation report for Aivilo ↔ Bedrock
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing || loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 disabled:opacity-50 transition-colors"
                >
                  <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {loading && (
                <div className="flex items-center justify-center h-48">
                  <FiRefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              )}

              {error && !loading && (
                <BedrockErrorAlert error={error} />
              )}

              {!loading && report && (
                <div className={`space-y-6 relative ${refreshing ? 'opacity-60 pointer-events-none' : ''}`}>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <SummaryCard
                      title="Synced"
                      count={summary.synced_correctly ?? 0}
                      icon={FiCheckCircle}
                      variant="success"
                      description="Correctly synchronized"
                    />
                    <SummaryCard
                      title="Issues"
                      count={issueCount}
                      icon={FiAlertTriangle}
                      variant={(summary.sync_errors ?? 0) > 0 ? 'error' : 'warning'}
                      description="Require attention"
                    />
                    <SummaryCard
                      title="In Aivilo Only"
                      count={summary.orphaned_in_aivilo ?? 0}
                      icon={FiUploadCloud}
                      variant="info"
                      description="Not synced to Bedrock"
                    />
                    <SummaryCard
                      title="In Bedrock Only"
                      count={summary.untracked_in_bedrock ?? 0}
                      icon={FiDownloadCloud}
                      variant="info"
                      description="Not imported to Aivilo"
                    />
                  </div>

                  {/* Sync All Button */}
                  {((summary.orphaned_in_aivilo ?? 0) > 0 || (summary.sync_errors ?? 0) > 0) && (
                    <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          Sync all pending prompts
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          Push all unsynced prompts to Bedrock
                        </p>
                      </div>
                      <button
                        onClick={handleSyncAllPending}
                        disabled={syncingAll}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
                      >
                        {syncingAll ? (
                          <>
                            <FiRefreshCw className="w-4 h-4 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <FiUploadCloud className="w-4 h-4" />
                            Sync All Pending
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Sync Results */}
                  {syncResults && (
                    <div className={`p-4 rounded-lg border ${
                      syncResults.failed > 0
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    }`}>
                      <div className="flex items-center gap-2">
                        {syncResults.failed > 0 ? (
                          <FiAlertTriangle className="w-5 h-5 text-amber-500" />
                        ) : (
                          <FiCheckCircle className="w-5 h-5 text-green-500" />
                        )}
                        <div>
                          <p className={`text-sm font-medium ${
                            syncResults.failed > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'
                          }`}>
                            Sync completed: {syncResults.successful} successful, {syncResults.failed} failed
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {syncResults.total_attempted} prompts processed
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {recommendations.length > 0 && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                        Recommendations
                      </h3>
                      <ul className="space-y-1">
                        {recommendations.map((rec) => (
                          <li key={rec} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span className="text-blue-500">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Issues */}
                  {issues.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                        Issues ({issues.length})
                      </h3>
                      <div className="space-y-3">
                        {issues.map((issue) => (
                          <IssueRow
                            key={`${issue.type}-${issue.prompt_id ?? issue.bedrock_prompt_id ?? issue.mapping_id ?? issue.description}`}
                            issue={issue}
                            onAction={handleIssueAction}
                            actionLoading={actionLoading}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All synced message */}
                  {issues.length === 0 && (summary.synced_correctly ?? 0) > 0 && (
                    <div className="p-6 text-center bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <FiCheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <h3 className="text-lg font-medium text-green-700 dark:text-green-300">
                        All Synced!
                      </h3>
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                        All {summary.synced_correctly} prompts are correctly synchronized with Bedrock.
                      </p>
                    </div>
                  )}

                  {/* Healthy Prompts (collapsible) */}
                  {healthyPrompts.length > 0 && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                      <button
                        onClick={() => setShowHealthy(!showHealthy)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <FiCheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            Healthy Prompts ({healthyPrompts.length})
                          </span>
                        </div>
                        <FiRefreshCw className={`w-4 h-4 text-gray-400 transition-transform ${showHealthy ? 'rotate-180' : ''}`} />
                      </button>
                      {showHealthy && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-2 max-h-64 overflow-y-auto">
                          {healthyPrompts.map((prompt) => (
                            <HealthyPromptRow key={prompt.prompt_name} prompt={prompt} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {report.generated_at && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                      Report generated at {new Date(report.generated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {!loading && !error && !report && (
                <div className="text-center py-12">
                  <FiLink className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No reconciliation data available.
                  </p>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 disabled:opacity-50 transition-colors"
                  >
                    {refreshing ? 'Loading...' : 'Run Reconciliation'}
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>
                  Total: {summary.total_aivilo_prompts ?? 0} in Aivilo, {summary.total_bedrock_prompts ?? 0} in Bedrock
                </span>
                <a
                  href="https://console.aws.amazon.com/bedrock/home#/prompt-management"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-blue-500 transition-colors"
                >
                  <FiExternalLink className="w-3.5 h-3.5" />
                  AWS Console
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BedrockReconciliationReport;
